import React, { useState } from 'react';
import {
  SimpleGrid,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Box,
  Stack,
  Tag,
  Heading,
} from '@chakra-ui/react';
import { FormInputs } from '../types/formInputs';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { tripErrandOptions } from '../data/options';
import { useSearchEmployeeQuery } from 'api/services/TravelForm';
import { EmployeeData, ProjectSearchDimension } from 'api/types/types';
import { Select, AsyncSelect, Input } from '@landsvirkjun/react.common';
import { useGetCalculateDimensions } from 'api/services/Dimensions';
import { LoadingContainer } from 'components/LoadingContainer/LoadingContainer';

type BasicInformationSectionProps = {
  errors: FieldErrors<FormInputs>;
  register: UseFormRegister<FormInputs>;
  setValue: UseFormSetValue<FormInputs>;
  watch: UseFormWatch<FormInputs>;
  control: Control<FormInputs, any>;
  mapApproverToUser: (dimension: string, adUser: string) => void;
  listOfApprovers:
    | {
        label: string;
        value: string;
        id: string;
      }[]
    | undefined;
  setEmplId: React.Dispatch<React.SetStateAction<string>>;
  editMode: any;
};

export const BasicInformationSection = ({
  errors,
  register,
  setValue,
  watch,
  control,
  mapApproverToUser,
  listOfApprovers,
  setEmplId,
  editMode,
}: BasicInformationSectionProps): JSX.Element => {
  const [userEmployeeId, setUserEmployeeId] = useState('');
  const { mutateAsync } = useSearchEmployeeQuery();
  const { refetch: getCalculatedDimensions } =
    useGetCalculateDimensions(userEmployeeId);

  const calculateDimensions = async (
    emplId: string,
    name: string
  ): Promise<void> => {
    try {
      await new Promise<void>((resolve) => {
        setUserEmployeeId(emplId);
        setEmplId(emplId);
        setValue('employeeName', name);
        resolve();
      });
      await getCalculatedDimensions().then((res) => {
        const dimensions = res.data?.Data;
        dimensions?.map((dimension: ProjectSearchDimension) => {
          if (dimension.type === 'Location') {
            setValue('workLocation', dimension.Name || '');
            setValue('workLocationId', dimension.id || '');
          }
          if (dimension.type === 'Department') {
            setValue('workDepartment', dimension.Name || '');
            setValue('workDepartmentId', dimension.id || '');
          }
        });
      });
    } catch (error) {
      console.error('Error fetching calculated dimensions:', error);
    }
  };

  const tripErrand = watch('tripErrand');
  const employee = watch('employeeName');
  const approverName = watch('approverName');
  console.log('list of approvers', listOfApprovers);
  return (
    <Box as="div">
      <Stack direction="row" pb={10} pt={5}>
        <Tag borderRadius="full">1</Tag>
        <Heading as="h3" size="md">
          Grunnupplýsingar
        </Heading>
      </Stack>
      <SimpleGrid spacing={10} columns={{ base: 1, md: 2 }}>
        <FormControl isInvalid={!!errors.employeeName}>
          <FormLabel>Starfsmaður</FormLabel>
          <Controller
            control={control}
            name="employeeName"
            render={(): JSX.Element => (
              <AsyncSelect
                placeholder="Leita eftir starfsmanni"
                mutateAsync={mutateAsync}
                disabled={editMode}
                onChange={(value): void => {
                  const employeeData = JSON.parse(value.value) as EmployeeData;
                  // Name is PascalCase — backend emits "Name" to satisfy AsyncSelect's label builder
                  const {
                    emplId,
                    Name: name,
                    dimensions,
                    adUser,
                    ssn,
                  } = employeeData;
                  setValue('employeeName', name);
                  setValue('employeeId', adUser);
                  setValue('ssn', ssn);
                  mapApproverToUser(dimensions[0], adUser);
                  calculateDimensions(emplId, name);
                }}
                defaultValue={
                  employee
                    ? {
                        label: employee,
                        value: employee,
                      }
                    : null
                }
                error={!!errors.employeeName}
              />
            )}
          />
          <FormErrorMessage>{errors.employeeName?.message}</FormErrorMessage>
        </FormControl>
        {listOfApprovers && listOfApprovers.length > 1 ? (
          <FormControl isInvalid={!!errors.approverName}>
            <FormLabel>Samþykkt af</FormLabel>
            <Controller
              control={control}
              name="approverName"
              render={(): JSX.Element => (
                <Select
                  onChange={(val: string): void => {
                    setValue('approverName', val);
                    const findApprover = listOfApprovers.find(
                      (el) => el.value === val
                    );
                    if (findApprover) setValue('approverId', findApprover.id);
                  }}
                  placeholder="Velja samþykkjanda"
                  options={listOfApprovers}
                  disabled={editMode}
                  defaultValue={
                    approverName
                      ? {
                          label: approverName,
                          value: approverName,
                        }
                      : null
                  }
                  error={!!errors.approverName}
                />
              )}
            />
            <FormErrorMessage>{errors.approverName?.message}</FormErrorMessage>
          </FormControl>
        ) : listOfApprovers && listOfApprovers.length === 1 ? (
          <FormControl>
            <FormLabel>Samþykkt af</FormLabel>
            <Input disabled {...register('approverName')} />
          </FormControl>
        ) : (
          <FormControl isInvalid>
            <FormLabel>Samþykkt af</FormLabel>
            <LoadingContainer isLoaded={!listOfApprovers}>
              <Input disabled {...register('approverName')} isInvalid />
              <FormErrorMessage>
                Ekki tókst að sækja samþykkjendur. Reyndu aftur síðar
              </FormErrorMessage>
            </LoadingContainer>
          </FormControl>
        )}
        <FormControl isInvalid={!!errors.tripErrand}>
          <FormLabel>Veldu erindi</FormLabel>
          <Controller
            control={control}
            name="tripErrand"
            render={({ field: { onChange } }): JSX.Element => (
              <Select
                onChange={onChange}
                options={tripErrandOptions}
                disabled={editMode}
                defaultValue={
                  tripErrand
                    ? {
                        label: tripErrand,
                        value: tripErrand,
                      }
                    : null
                }
                error={!!errors.tripErrand}
              />
            )}
          />
          <FormErrorMessage>{errors.tripErrand?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors.tripPurpose}>
          <FormLabel>Tilgangur ferðar</FormLabel>
          <Input
            placeholder="Settu inn lýsingu ferðar"
            disabled={editMode}
            {...register('tripPurpose')}
            _focus={{ boxShadow: 'none !important' }}
          />
          <FormErrorMessage>{errors.tripPurpose?.message}</FormErrorMessage>
        </FormControl>
      </SimpleGrid>
    </Box>
  );
};
